import { useCallback, useEffect, useState } from "react";
import { Inbox, LogOut, RefreshCw, Trash2 } from "lucide-react";
import { Eyebrow } from "./Reveal";
import QuoteCard from "./QuoteCard";
import { api } from "../lib/api";

const AdminInbox = ({ user, onLogout }) => {
    const [quotes, setQuotes] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [confirmClear, setConfirmClear] = useState(false);
    const [error, setError] = useState("");
    const [view, setView] = useState("pending");

    const pending = quotes?.filter((q) => !q.called_at) ?? [];
    const called = quotes?.filter((q) => q.called_at) ?? [];
    const shown = view === "pending" ? pending : called;

    const load = useCallback(async () => {
        setError("");
        try {
            const { data } = await api.get("/admin/quotes");
            setQuotes(data);
        } catch (err) {
            if (err.response?.status === 401) onLogout();
            else setError("Couldn't load quotes. Try refreshing.");
        }
    }, [onLogout]);

    useEffect(() => {
        load();
    }, [load]);

    const remove = async (id) => {
        setBusyId(id);
        try {
            await api.delete(`/admin/quotes/${id}`);
            setQuotes((q) => q.filter((x) => x.id !== id));
        } catch {
            setError("Couldn't delete that quote.");
        } finally {
            setBusyId(null);
        }
    };

    const toggleCalled = async (id, nextCalled) => {
        setBusyId(id);
        try {
            const { data } = await api.patch(`/admin/quotes/${id}/called`, { called: nextCalled });
            setQuotes((q) => q.map((x) => (x.id === id ? data : x)));
        } catch {
            setError("Couldn't update that quote.");
        } finally {
            setBusyId(null);
        }
    };

    const clearAll = async () => {
        setBusyId("all");
        try {
            await api.delete("/admin/quotes");
            setQuotes([]);
            setConfirmClear(false);
        } catch {
            setError("Couldn't clear the inbox.");
        } finally {
            setBusyId(null);
        }
    };

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        onLogout();
    };

    return (
        <div data-testid="admin-inbox">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                <div>
                    <Eyebrow className="mb-4">Signed in as {user.email}</Eyebrow>
                    <h1 className="font-display font-black uppercase text-brand-cream text-5xl sm:text-6xl tracking-tight leading-[0.92]">
                        Quote <span className="text-brand-amber">inbox.</span>
                    </h1>
                    <p className="mt-4 text-brand-sage text-base" data-testid="admin-quote-count">
                        {quotes === null
                            ? "Loading..."
                            : `${pending.length} pending · ${called.length} called back`}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={load}
                        data-testid="admin-refresh-btn"
                        className="inline-flex items-center gap-2 border border-brand-line text-brand-sage text-sm px-4 py-2.5 rounded-full hover:border-brand-amber hover:text-brand-amber transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                    {quotes?.length > 0 &&
                        (confirmClear ? (
                            <>
                                <button
                                    onClick={clearAll}
                                    disabled={busyId === "all"}
                                    data-testid="admin-clear-confirm-btn"
                                    className="inline-flex items-center gap-2 bg-[#ff9b7a] text-brand-ink font-bold text-sm px-4 py-2.5 rounded-full disabled:opacity-60"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Yes, clear all {quotes.length}
                                </button>
                                <button
                                    onClick={() => setConfirmClear(false)}
                                    data-testid="admin-clear-cancel-btn"
                                    className="text-brand-sage text-sm px-4 py-2.5 hover:text-brand-cream"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => setConfirmClear(true)}
                                data-testid="admin-clear-all-btn"
                                className="inline-flex items-center gap-2 border border-brand-line text-brand-sage text-sm px-4 py-2.5 rounded-full hover:border-[#ff9b7a] hover:text-[#ff9b7a] transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                                Clear all
                            </button>
                        ))}
                    <button
                        onClick={logout}
                        data-testid="admin-logout-btn"
                        className="inline-flex items-center gap-2 border border-brand-line text-brand-sage text-sm px-4 py-2.5 rounded-full hover:border-brand-amber hover:text-brand-amber transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                        Sign out
                    </button>
                </div>
            </div>

            {error && (
                <p className="mb-6 text-sm text-[#ff9b7a]" data-testid="admin-error">
                    {error}
                </p>
            )}

            {quotes && (
                <div className="flex gap-2 mb-6" data-testid="admin-inbox-filter">
                    {[
                        { key: "pending", label: `Pending (${pending.length})` },
                        { key: "called", label: `Called back (${called.length})` },
                    ].map((v) => (
                        <button
                            key={v.key}
                            onClick={() => setView(v.key)}
                            data-testid={`admin-filter-${v.key}`}
                            className={`font-mono text-[11px] uppercase tracking-[0.2em] rounded-full px-4 py-2 border transition-colors ${
                                view === v.key
                                    ? "border-brand-amber text-brand-amber bg-brand-amber/10"
                                    : "border-brand-line text-brand-sage hover:text-brand-cream"
                            }`}
                        >
                            {v.label}
                        </button>
                    ))}
                </div>
            )}

            {quotes && shown.length === 0 && (
                <div
                    className="rounded-lg border border-dashed border-brand-line p-16 text-center"
                    data-testid="admin-empty-state"
                >
                    <Inbox className="w-10 h-10 text-brand-moss mx-auto mb-4" />
                    <p className="font-display font-bold uppercase text-brand-cream text-2xl tracking-tight">
                        {view === "pending" ? "Nothing pending." : "No called-back requests yet."}
                    </p>
                    <p className="text-brand-sage text-sm mt-2">
                        {view === "pending"
                            ? "New website quote requests will show up here and in your email."
                            : "Hit \"Mark called\" on a pending request and it moves here."}
                    </p>
                </div>
            )}

            <div className="space-y-4">
                {shown.map((q) => (
                    <QuoteCard
                        key={q.id}
                        quote={q}
                        onDelete={remove}
                        onToggleCalled={toggleCalled}
                        deleting={busyId === q.id}
                    />
                ))}
            </div>
        </div>
    );
};

export default AdminInbox;
